import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import ApiCall from "../../utils/apicall";
import { HomeRounded } from "@mui/icons-material";
import Iconify from "../../components/iconify";
import { hasPermission, PERMISSIONS } from "../../utils/hasPermission";
import { useSelector } from "react-redux";
import {
  Card,
  Stack,
  Paper,
  Typography,
  Button,
  Breadcrumbs,
  Container,
  TableContainer,
  TableCell,
  Table,
  TableRow,
  TableBody,
  TableHead,
  MenuItem,
} from "@mui/material";
import DrawingViewer from "../../components/JobDrawing/DrawingViewer";
import DrawingsTable from "../../components/JobDrawing/DrawingsTable";
import DocumentUpload from "../Documents/DocumentUpload";
import { listDrawingsForJob } from "../../utils/documentsApi";

/**
 * Detail / Fabricated Items page.
 *
 * Phase 1 of route merge (2026-05-05): can be rendered standalone (legacy
 * /detail-Job/:id route) OR embedded inside the Detailing Workspace's
 * "Fabricated Items" tab. When `embedded` prop is true, suppress the outer
 * Container/Breadcrumbs/Helmet so the parent provides them.
 */
function Detail({ embedded = false }) {
  const { id: paramId } = useParams();
  // When embedded, parent passes jobId via prop or URL; useParams still works
  // because embedded usage is at /jobs/:id/detailing where :id matches.
  const id = paramId;
  const [fabricateditems, setfabricatedItems] = useState(null);
  const [Poitems, setPoItems] = useState(null);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { loginUser } = useSelector((state) => state.userSlice);
  const { permissions: userPermissions } = loginUser.decodedToken;
  const canAddFabricatedItems = hasPermission(
    userPermissions,
    PERMISSIONS.Add_Fabricated_Items
  );
  // S-64 (2026-10-06): replacing or removing a job's drawing needs edit_job on the server; the first
  // upload stays open to anyone who can view the job.
  // S-66: drawing markup (save / clear) needs edit_job too; the server checks the same slug, live.
  const canEditJob = hasPermission(userPermissions, PERMISSIONS.EDIT_JOB);

  // ── Drawing PDF state ──────────────────────────────────────────────────────
  const [drawingUrl, setDrawingUrl] = useState(null);
  // S-66: the job's drawing file name (jobs.drawing_pdf): the revision markup is tied to.
  const [drawingFile, setDrawingFile] = useState(null);
  const [viewerOpen, setViewerOpen] = useState(false);
  const [uploadStatus, setUploadStatus] = useState("");
  const fileInputRef = useRef(null);

  // ── Drawings library state (multi-drawing per job) ────────────────────────
  // Backed by GET /api/job/:id/drawings; falls back gracefully if backend
  // hasn't shipped the endpoint yet (see utils/documentsApi.js).
  const [drawings, setDrawings] = useState([]);
  const [drawingsLoading, setDrawingsLoading] = useState(true);
  const [drawingsNotAvailable, setDrawingsNotAvailable] = useState(false);
  const [drawingsError, setDrawingsError] = useState(null);
  const [drawingsRefreshKey, setDrawingsRefreshKey] = useState(0);

  // Load job to get drawing URL. Codex #75 r2 P1: a job change closes the viewer and forgets the
  // previous job's drawing; a late reply for another job is ignored.
  const currentIdRef = useRef(id);
  currentIdRef.current = id;
  useEffect(() => {
    let cancelled = false;
    setViewerOpen(false);
    setDrawingUrl(null);
    setDrawingFile(null);
    ApiCall.get(`/job/${id}`)
      .then((res) => {
        if (cancelled) return;
        const job = res.data?.data?.job;
        if (job?.drawing_pdf_url) setDrawingUrl(job.drawing_pdf_url);
        setDrawingFile(job?.drawing_pdf || null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [id]);

  // Load the Drawings library for this job (new multi-drawing endpoint).
  // Backend spec: outputs/SFM-drawings-backend-spec.md §2.1.
  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setDrawingsLoading(true);
    setDrawingsError(null);
    setDrawingsNotAvailable(false);
    listDrawingsForJob(id).then((result) => {
      if (cancelled) return;
      setDrawings(result.documents || []);
      setDrawingsNotAvailable(!!result.notAvailable);
      setDrawingsError(result.error || null);
      setDrawingsLoading(false);
    });
    return () => { cancelled = true; };
  }, [id, drawingsRefreshKey]);

  const handleDrawingUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (file.type !== "application/pdf") {
      setUploadStatus("Only PDF files are accepted.");
      return;
    }
    setUploadStatus("Uploading…");
    const formData = new FormData();
    formData.append("drawing", file);
    try {
      const res = await ApiCall.post(`/job/${id}/drawing`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const url = res.data?.data?.drawing_pdf_url;
      setDrawingUrl(url);
      setDrawingFile(res.data?.data?.drawing_pdf || null);
      setUploadStatus("Drawing uploaded ✓");
      setTimeout(() => setUploadStatus(""), 3000);
    } catch (err) {
      setUploadStatus("Upload failed. Try again.");
    }
    // Reset file input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Codex #75 r1 P2: opening the viewer re-reads the job's current drawing first, so reopening after the
  // drawing was replaced elsewhere shows the new drawing (and its markup), not the cached one.
  // Codex #75 r4 P2: one open at a time (the button shows "Opening…" and is disabled); closing the viewer
  // or a newer open invalidates a pending one.
  const [opening, setOpening] = useState(false);
  const openSeq = useRef(0);
  const closeViewer = () => { openSeq.current += 1; setOpening(false); setViewerOpen(false); };
  const openViewer = async () => {
    if (opening) return;
    const forId = id;
    const seq = ++openSeq.current;
    setOpening(true);
    try {
      await openViewerFor(forId, seq);
    } finally {
      if (openSeq.current === seq) setOpening(false);
    }
  };
  const openViewerFor = async (forId, seq) => {
    try {
      const res = await ApiCall.get(`/job/${forId}`);
      if (currentIdRef.current !== forId || openSeq.current !== seq) return; // navigated away or superseded
      const job = res.data?.data?.job;
      if (job && job.id !== undefined) {
        setDrawingUrl(job.drawing_pdf_url || null);
        setDrawingFile(job.drawing_pdf || null);
        if (!job.drawing_pdf_url) {
          setUploadStatus("This job no longer has a drawing.");
          return;
        }
      }
    } catch { /* open with what is already loaded; the viewer checks the revision with the server */ }
    if (currentIdRef.current !== forId || openSeq.current !== seq) return;
    setViewerOpen(true);
  };

  const handleRemoveDrawing = async () => {
    if (!window.confirm("Remove the drawing PDF from this job?")) return;
    try {
      await ApiCall.delete(`/job/${id}/drawing`);
      setDrawingUrl(null);
      setDrawingFile(null);
    } catch {}
  };
  // ─────────────────────────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await ApiCall.get(
          `/fabricated-items/get-fabricated-item/${id}`
        );
        if (res.data.data.FabricatedItems.length > 0) {
          setfabricatedItems(res.data.data.FabricatedItems);
        } else {
          setError("ss");
        }
      } catch (error) {
        setError(error);
      }
    };
    fetchData();
  }, [id]);

  useEffect(() => {
    const fetchItems = async () => {
      try {
        const res = await ApiCall.get(
          `/fabricated-items/get-poitem-perjob/${id}`
        );
        setPoItems(res.data.data);
      } catch (error) {
        setError(error);
      }
    };
    fetchItems();
  }, [id]);

  const handleBackClick = () => navigate("/jobs");
  const handleOpenCreate = (id) => navigate(`/create-items/${id}`);
  const handleDetails = (uniqueName, correspondingItem, id) => {
    navigate(`/fabricated-item-details/${uniqueName}`, {
      state: { correspondingItem, id },
    });
  };

  // Inner content (action bar + tables + drawings + upload). Always rendered.
  // When embedded, it sits directly inside the parent Container; standalone
  // usage wraps it in Container + Breadcrumbs below.
  const inner = (
    <>
      {/* ── Action bar: Add Item + Drawing buttons ─────────────────────── */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 8,
            marginBottom: 8,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            {canAddFabricatedItems && (
              <Button
                onClick={() => handleOpenCreate(id)}
                variant="contained"
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Add Item
              </Button>
            )}
            <Button
              onClick={() => navigate(`/heat-management/${id}`)}
              variant="outlined"
              startIcon={<Iconify icon="eva:layers-outline" />}
            >
              Heats
            </Button>
            <Button
              onClick={() => navigate(`/kiosk/${id}`)}
              variant="outlined"
              startIcon={<Iconify icon="eva:smartphone-outline" />}
            >
              Kiosk View
            </Button>
            <Button
              onClick={() => navigate(`/release-packages/${id}`)}
              variant="outlined"
              startIcon={<Iconify icon="eva:shield-outline" />}
            >
              Release Packages
            </Button>
          </Stack>

          {/* Drawing PDF controls */}
          <Stack direction="row" spacing={1} alignItems="center">
            {drawingUrl && (
              <Button
                variant="contained"
                color="success"
                startIcon={<Iconify icon="eva:book-open-outline" />}
                onClick={openViewer}
                disabled={opening}
              >
                {opening ? "Opening…" : "View Drawing"}
              </Button>
            )}

            {/* Hidden file input */}
            <input
              ref={fileInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: "none" }}
              onChange={handleDrawingUpload}
            />
            {(!drawingUrl || canEditJob) && (
              <Button
                variant={drawingUrl ? "outlined" : "contained"}
                color="primary"
                startIcon={<Iconify icon="eva:upload-outline" />}
                onClick={() => fileInputRef.current && fileInputRef.current.click()}
              >
                {drawingUrl ? "Replace Drawing" : "Upload Drawing PDF"}
              </Button>
            )}

            {drawingUrl && canEditJob && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={handleRemoveDrawing}
              >
                Remove
              </Button>
            )}
          </Stack>
        </div>

        {/* Upload status message */}
        {uploadStatus && (
          <Typography
            variant="body2"
            style={{
              color: uploadStatus.includes("✓") ? "#2e7d32" : "#c62828",
              marginBottom: 8,
            }}
          >
            {uploadStatus}
          </Typography>
        )}

        <Paper>
          {error ? (
            <Typography variant="body1" style={{ marginTop: "10px" }}>
              Fabricated Item Not found
            </Typography>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Fabricated Item Name</TableCell>
                    <TableCell></TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fabricateditems &&
                    [...new Set(fabricateditems.map((item) => item.name))].map(
                      (uniqueName, index) => {
                        const correspondingItem = fabricateditems.find(
                          (item) => item.name === uniqueName
                        );
                        return (
                          <React.Fragment key={index}>
                            <TableRow>
                              <TableCell>{uniqueName}</TableCell>
                              <TableCell
                                align="right"
                                style={{ display: "flex" }}
                              >
                                <MenuItem
                                  sx={{ color: "error.main" }}
                                  onClick={() =>
                                    handleDetails(
                                      uniqueName,
                                      correspondingItem.id,
                                      id
                                    )
                                  }
                                >
                                  <Iconify icon={"eva:info-outline"} />
                                </MenuItem>
                              </TableCell>
                            </TableRow>
                          </React.Fragment>
                        );
                      }
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>

        <Paper style={{ marginTop: "10px" }}>
          <Card>
            {Poitems && Poitems.length > 0 ? (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>PurchaseOrder Items</TableCell>
                      <TableCell>Quantity</TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                      <TableCell></TableCell>
                    </TableRow>
                  </TableHead>
                  {Poitems.map((item, index) => (
                    <TableBody key={index}>
                      <TableRow>
                        <TableCell>{item.itemName}</TableCell>
                        <TableCell>{item.quantity}</TableCell>
                      </TableRow>
                    </TableBody>
                  ))}
                </Table>
              </TableContainer>
            ) : (
              <Typography variant="body1"></Typography>
            )}
          </Card>
        </Paper>

        {/* ── Drawings library for this job ─────────────────────────────── */}
        <Paper sx={{ mt: 2, p: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Drawings
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {drawings.length > 0 ? `${drawings.length} drawing${drawings.length === 1 ? "" : "s"}` : ""}
            </Typography>
          </Stack>
          <DrawingsTable
            documents={drawings}
            loading={drawingsLoading}
            notAvailable={drawingsNotAvailable}
            error={drawingsError}
            emptyMessage={
              drawingUrl
                ? "No drawings in the library yet — this job has a single legacy drawing PDF attached (see above)."
                : "No drawings uploaded yet. Use the upload section below to add engineering packages."
            }
            showPmk
            showFabItem
          />
        </Paper>

        {/* Bulk Document Upload — embedded in Job Detail */}
        <Paper sx={{ mt: 2, p: 2 }}>
          <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
            Upload Engineering Packages
          </Typography>
          <DocumentUpload
            preselectedJobId={parseInt(id, 10)}
            embedded
            onUploadComplete={() => setDrawingsRefreshKey((k) => k + 1)}
          />
        </Paper>

        {!embedded && (
          <Button
            variant="contained"
            color="primary"
            style={{ marginTop: "10px" }}
            onClick={handleBackClick}
          >
            Back
          </Button>
        )}
    </>
  );

  // EMBEDDED MODE: parent supplies Container + breadcrumbs + helmet
  if (embedded) {
    return (
      <>
        {/* Full-screen PDF viewer — outside Container so it covers everything */}
        {viewerOpen && drawingUrl && (
          <DrawingViewer key={drawingFile || "none"} url={drawingUrl} jobId={id} drawingFile={drawingFile} canEdit={canEditJob} onClose={closeViewer} />
        )}
        {inner}
      </>
    );
  }

  // STANDALONE MODE (legacy /detail-Job/:id; still works but Jobs list now
  // routes to /jobs/:id/detailing?tab=fabricated-items which embeds this).
  return (
    <div>
      <Helmet>
        <title>Fabricated Items | SFM</title>
      </Helmet>
      {viewerOpen && drawingUrl && (
        <DrawingViewer key={drawingFile || "none"} url={drawingUrl} jobId={id} drawingFile={drawingFile} canEdit={canEditJob} onClose={closeViewer} />
      )}
      <Container>
        <Stack direction="row" alignItems="center" justifyContent="space-between" mb={5}>
          <Breadcrumbs aria-label="breadcrumb">
            <Stack direction="row" alignItems="center" spacing={1}>
              <HomeRounded color="inherit" />
              <Typography variant="body1" color="textPrimary">/ Fabricated Items</Typography>
            </Stack>
          </Breadcrumbs>
        </Stack>
        {inner}
      </Container>
    </div>
  );
}

export default Detail;
